import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { inferAdminModeForRow } from "./admin-event-prefill";
import { apiJson } from "./api";

type Row = Record<string, unknown>;

function text(v: unknown): string {
  return String(v ?? "").trim();
}

function parseJsonObj(v: unknown): Record<string, unknown> {
  try {
    const parsed = JSON.parse(String(v ?? "{}")) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function fmtDateTime(v: unknown): string {
  const raw = text(v);
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" });
}

function correctionEditRoute(r: Row): string {
  const snapshot = parseJsonObj(r.snapshot_json);
  const mode = inferAdminModeForRow(snapshot);
  return `/admin/events/${mode}?edit=${encodeURIComponent(text(r.event_id))}`;
}

function CorrectionCard({ row, busyId, onAction }: { row: Row; busyId: string | null; onAction: (id: string, path: string, body: object) => Promise<void> }) {
  const id = text(row.id);
  const pending = text(row.status) === "pending";
  const patch = parseJsonObj(parseJsonObj(row.proposed_json).patch ? JSON.stringify(parseJsonObj(row.proposed_json).patch) : "{}");
  const snapshot = parseJsonObj(row.snapshot_json);
  const title = text(snapshot.event_name) || text(row.event_id) || "対象イベント";

  return (
    <article className="card correction-report-card">
      <div className="row" style={{ justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>{title}</h3>
          <p className="meta" style={{ margin: 0 }}>
            {fmtDateTime(row.created_at)} ・ 状態: {text(row.status) || "—"}
          </p>
        </div>
        {pending ? <span className="pill mono">未対応</span> : <span className="pill mono">対応済み</span>}
      </div>

      <p style={{ margin: "10px 0", fontSize: 14, whiteSpace: "pre-wrap" }}>{text(row.message)}</p>

      {Object.keys(patch).length ? (
        <div className="meta" style={{ lineHeight: 1.65 }}>
          <strong>修正候補</strong>
          {Object.entries(patch).map(([k, v]) => (
            <div key={k}>
              {k}: {text(v) || "—"}
            </div>
          ))}
        </div>
      ) : (
        <p className="meta">修正候補の入力はありません。メッセージを確認してください。</p>
      )}

      <div className="row" style={{ marginTop: 10, gap: 8, flexWrap: "wrap" }}>
        {text(row.event_id) ? (
          <Link className="btn small secondary" to={correctionEditRoute(row)}>
            予定を編集
          </Link>
        ) : null}
        {pending ? (
          <>
            <button type="button" className="btn small" disabled={busyId === id} onClick={() => onAction(id, "/apply", {})}>
              修正候補をDBに反映
            </button>
            <button type="button" className="btn small secondary" disabled={busyId === id} onClick={() => onAction(id, "/apply", { acknowledgeOnly: true })}>
              確認済みにする
            </button>
            <button type="button" className="btn small secondary" disabled={busyId === id} onClick={() => onAction(id, "/reject", { note: "管理画面で却下" })}>
              却下
            </button>
          </>
        ) : null}
      </div>
    </article>
  );
}

export function CorrectionReportsPage() {
  const [status, setStatus] = useState<"pending" | "all">("pending");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await apiJson<{ requests: Row[] }>(`/api/admin/event-requests?status=${status}`);
      setRows((data.requests ?? []).filter((r) => text(r.kind) === "correction"));
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  const sorted = useMemo(() => [...rows].sort((a, b) => text(b.created_at).localeCompare(text(a.created_at))), [rows]);

  const onAction = async (id: string, path: string, body: object) => {
    setBusyId(id);
    setErr(null);
    try {
      await apiJson(`/api/admin/event-requests/${encodeURIComponent(id)}${path}`, { method: "POST", body: JSON.stringify(body) });
      await load();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="card" style={{ padding: "14px 16px" }}>
      <h2 style={{ margin: "0 0 8px", fontSize: 16 }}>間違ってるよ！報告</h2>
      <p className="meta" style={{ lineHeight: 1.6 }}>
        表サイトの予定一覧から送信された「間違ってるよ！」報告を管理します。
      </p>
      <div className="row" style={{ gap: 8, margin: "10px 0 12px", flexWrap: "wrap" }}>
        <button type="button" className={`chip${status === "pending" ? " active" : ""}`} onClick={() => setStatus("pending")}>
          未対応
        </button>
        <button type="button" className={`chip${status === "all" ? " active" : ""}`} onClick={() => setStatus("all")}>
          全て
        </button>
        <span className="pill mono">{sorted.length}件</span>
      </div>
      {loading ? <p className="meta">読み込み中…</p> : null}
      {err ? <p style={{ color: "#b00020" }}>{err}</p> : null}
      {!loading && sorted.length === 0 ? <p className="meta">報告はありません。</p> : null}
      <div className="correction-report-list">
        {sorted.map((row) => (
          <CorrectionCard key={text(row.id)} row={row} busyId={busyId} onAction={onAction} />
        ))}
      </div>
    </section>
  );
}
